import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import TokenIcon from '../TokenIcon';
import CopyWrapper from '../CopyWrapper';
import formatCurrency from '../../utils/currencyFormatter';
import { ReactComponent as ArrowDown } from '../../img/general-icon/arrow-down.svg';
import ReactPaginate from 'react-paginate';
import { ReactComponent as CopyGrayIcon } from '../../img/default-svg/copy.svg';
import { ReactComponent as ArrowLong } from '../../img/wallets/arrow-long.svg';
import { ReactComponent as ArrowLong1 } from '../../img/wallets/arrow-long-1.svg';
import settingsIcon from '../../img/default-svg/settings.svg';
import trashIcon from '../../img/default-svg/trash.svg';
import lockedIcon from '../../img/default-svg/lock-slash.svg';
import unlockedIcon from '../../img/default-svg/lockIcon.svg';
import unlockedIconInex from '../../img/inex/lockIcon.svg';
import unlockedIconInsta from '../../img/instaXchange/lockIcon.svg';
import unlockedIconCoinera from '../../img/coinera/lockIcon.svg';
import autosign from '../../img/default-svg/autosign.svg';
import autosignOffIcon from '../../img/default-svg/autosign-off.svg';
import autosignOffInex from '../../img/inex/autosign-off.svg';
import autosignOffInsta from '../../img/instaXchange/autosign-off.svg';
import autosignOffCoinera from '../../img/coinera/autosign-off.svg';
import { identity } from '../../utils/getIdentity';
import { cryptoApi } from '../../service/cryptopay-api';
import './WalletsNewList.scss';
import '../Pagination/Pagination.scss';
import { tokenNodeTypeObj, tokenTypeEnd } from "../../utils/tokenNodeTypes";

const autosignOff = {
  cpay: autosignOffIcon,
  nfg: autosignOffIcon,
  finvaro: autosignOffIcon,
  clarnium: autosignOffIcon,
  inex: autosignOffInex,
  insta: autosignOffInsta,
  coinera: autosignOffCoinera,
};

const lockIcon = {
  cpay: unlockedIcon,
  nfg: unlockedIcon,
  finvaro: unlockedIcon,
  clarnium: unlockedIcon,
  inex: unlockedIconInex,
  insta: unlockedIconInsta,
  coinera: unlockedIconCoinera,
};

const WalletsNewListSecurityCells = ({
  merchantId,
  wallet,
  openSignPassword,
  refreshedWalletId,
  onSecurityRefreshConsumed,
}) => {
  const [statusAutosign, setStatusAutosign] = useState(true);
  const [statusPassword, setStatusPassword] = useState(false);
  const location = typeof window !== 'undefined' ? window.location.href : '';
  const isAdmin = location?.includes('admin/');

  const loadStatus = useCallback(async () => {
    if (isAdmin || !merchantId || !wallet?._id) {
      return;
    }
    try {
      const response = await cryptoApi.checkAutosignStatus(merchantId, wallet._id);
      const data = await response.data;
      setStatusAutosign(data);
    } catch (e) {
      console.error(e);
    }
    try {
      const response = await cryptoApi.checkPasswordStatus(merchantId, wallet._id);
      const data = await response.data;
      setStatusPassword(data);
    } catch (e) {
      console.error(e);
      setStatusPassword(false);
    }
  }, [isAdmin, merchantId, wallet?._id]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!refreshedWalletId || refreshedWalletId !== wallet?._id) {
      return;
    }
    let cancelled = false;
    loadStatus().then(() => {
      if (!cancelled && onSecurityRefreshConsumed) {
        onSecurityRefreshConsumed();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [refreshedWalletId, wallet?._id, loadStatus, onSecurityRefreshConsumed]);

  return (
    <>
      <Col span={2} className="admin-wallet__security-cell admin-wallet__cell-pad-right">
        <img src={statusAutosign ? autosignOff[identity] : autosign} alt="" />
      </Col>
      <Col span={1} className="admin-wallet__security-cell">
        <img
          className="admin-wallet__security-lock"
          onClick={() => {
            if (statusAutosign && openSignPassword) {
              openSignPassword(wallet);
            }
          }}
          src={!statusPassword ? lockedIcon : lockIcon[identity]}
          alt=""
        />
      </Col>
    </>
  );
};

const WalletsNewList = ({
  wallets,
  page,
  pages,
  fetching,
  merchantId,
  networkFilter,
  currencyIds,
  getMerchantWallets,
  getMoreMerchantWallets,
  search,
  hideZeroCheckbox,
  walletVersion = 'v2',
  openSettings,
  openDelete,
  openSignPassword,
  securityRefreshedWalletId,
  onSecurityRefreshConsumed,
}) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpanded = (address) => {
    setExpandedItems((prev) => ({ ...prev, [address]: !prev[address] }));
  };

  const getGeneralAmount = (wallet) => {
    if (wallet?.balances?.length > 0) {
      return wallet.balances.reduce((acc, cur) => acc + cur?.balance?.usd, 0);
    }
    const tokensUsd = (wallet?.tokens || []).reduce((acc, cur) => acc + (cur?.balance?.usd || 0), 0);
    return (wallet?.balance?.usd || 0) + tokensUsd;
  };

  const getWalletSubAssets = (wallet) => {
    if (wallet?.balances?.length > 0) {
      return wallet.balances;
    }
    return (wallet?.tokens || []).map(token => ({
      currency: {
        name: token.currency,
        id: token.currencyId,
        nodeType: wallet.nodeType,
        currencyType: 'token',
      },
      balance: {
        value: token.balance.value,
        usd: token.balance.usd,
        hold: token.holdBalance,
      },
    }));
  };

  const fetchParams = {
    typeNetwork: networkFilter,
    walletVersion,
    order: 'DESC',
    limit: 10,
    currencyIds: currencyIds?.join(',') || undefined,
    typeWallet: 'merchant',
    showTokens: false,
    search: search || undefined,
    positiveBalance: hideZeroCheckbox,
  };

  const handlePageChange = (selected) => {
    getMoreMerchantWallets(merchantId, { ...fetchParams, page: selected.selected + 1 });
  };

  if (fetching) {
    return (
      <div className="admin-wallet">
        <div className="admin-wallet__loader">
          <div className="admin-wallet__spinner" />
        </div>
      </div>
    );
  }

  const walVer = localStorage.getItem('walletVersion') || 'unknown';

  if (!wallets?.length) {
    return null;
  }

  return (
    <div className="admin-wallet">
      <div className="admin-wallet__content">
        <Row align="middle" className="admin-wallet__rowTitle">
          <Col span={1} />
          <Col span={10}>{t('address')}</Col>
          <Col span={3}>{t('amount')}</Col>
          <Col span={4} className="admin-wallet__cell-pad-right">{t('dataTime')}</Col>
          <Col span={2} className="admin-wallet__rowTitle--security admin-wallet__cell-pad-right">
            {t('merchants.security.autosignStatus')}
          </Col>
          <Col span={1} className="admin-wallet__rowTitle--security">
            {t('merchants.security.passwordStatus')}
          </Col>
          <Col span={2} />
          <Col span={1} />
        </Row>
        {wallets && wallets?.length > 0 && wallets.map((item) => (
          <Row align="middle" className="admin-wallet__rowBody" key={item?._id || item?.address}>
            <Col
              className="admin-wallet__end"
              span={1}
              onClick={() => toggleExpanded(item.address)}
              style={{ cursor: "pointer", marginTop: "-3px" }}
            >
              {getWalletSubAssets(item).length > 0 && (
                <ArrowDown
                  color="#80827E"
                  style={{
                    transform: expandedItems[item.address] ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s"
                  }}
                />
              )}
            </Col>
            <Col span={10} className="admin-wallet__address">
              <TokenIcon tokenName={item?.chainSymbol} />
              <CopyWrapper copyContent={item?.address}>{item?.address}</CopyWrapper>
              <CopyWrapper copyContent={item?.address}><CopyGrayIcon /></CopyWrapper>
            </Col>
            <Col span={3}>{formatCurrency(+getGeneralAmount(item))}</Col>
            <Col span={4} className="admin-wallet__cell-pad-right">
              {moment(item?.createdAt).format('MMM D, YYYY, HH:mm') || '-'}
            </Col>
            <WalletsNewListSecurityCells
              merchantId={merchantId}
              wallet={item}
              openSignPassword={openSignPassword}
              refreshedWalletId={securityRefreshedWalletId}
              onSecurityRefreshConsumed={onSecurityRefreshConsumed}
            />
            <Col span={2} className={`${item?.main ? 'admin-wallet__default' : 'admin-wallet__secondary'}`}>
              {item?.main ? t('wallets.default') : t('wallets.secondary')}
            </Col>
            <Col className="admin-wallet__end" span={1}>
              <div className="admin-wallet__actions-wrap">
                <img
                  className="admin-wallet__action-icon"
                  src={settingsIcon}
                  alt="settings"
                  onClick={() => openSettings && openSettings(item)}
                />
                <img
                  className="admin-wallet__action-icon"
                  src={trashIcon}
                  alt="remove"
                  onClick={() => openDelete && openDelete(item)}
                />
              </div>
            </Col>

            <div className="list__row">
              {expandedItems[item?.address] && getWalletSubAssets(item).map((asset, index) => (
                <Row align="bottom" key={asset.id}>
                  <Col span={2}>{index === 0 ? <ArrowLong /> : <ArrowLong1 />}</Col>
                  <Col span={8} className={`tokens admin-wallet__relative`}><TokenIcon
                    className=""
                    tokenName={asset?.currency?.name} />
                    {asset?.currency?.name === "Binance-Peg BTCB Token" ? "BTCB" : asset?.currency?.name}
                    {' '}
                    {asset?.currency?.currencyType === 'token' ? tokenNodeTypeObj[asset?.currency?.nodeType] : null}
                    {tokenTypeEnd(asset?.currency?.currencyType, asset?.currency?.nodeType, asset?.currency?.name)}
                  </Col>
                  <Col className="admin-wallet__left" span={4}>
                    <span>{asset?.balance?.value} {asset?.currency?.name}</span>
                    <span className="admin-wallet__left--amount">({formatCurrency(+asset?.balance?.usd)})</span>
                  </Col>
                </Row>
              ))}
            </div>
          </Row>
        ))}
        {pages > 1 && (
          <ReactPaginate
            pageCount={pages}
            pageRangeDisplayed={5}
            marginPagesDisplayed={2}
            onPageChange={handlePageChange}
            containerClassName="pagination"
            activeClassName="active"
            previousLabel="«"
            nextLabel="»"
            breakLabel="..."
            forcePage={Math.max(0, (page || 1) - 1)}
          />
        )}
      </div>
    </div>
  );
};

export default WalletsNewList;
